import CompactInventoryCard from "@Components/CompactInventoryCard";
import { getLastViewedListings } from "@Pages/Search/data/helper";
import { useCallback, useEffect, useState } from "react";
import { defaultInventoryState } from "./data";
import classes from "./RecentelyViewedSection.module.scss";
import getInventories, { filterActiveChildInventories } from "./helper";
import SectionContainer from "@Pages/PropertyPage/components/SectionContainer";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import HorizontalScroll from "@Components/HorizontalScroll";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector } from "react-redux";

interface IProps {
  gtagEventCategory: string;
  isDesktop?: boolean;
  isSearchPage?: boolean;
  isSearchPageRails?: boolean;
}

const RecentelyViewedSection = (props: IProps) => {
  const { gtagEventCategory, isDesktop, isSearchPage, isSearchPageRails = false } = props;
  const [recentListings, setRecentListings] = useState([]);
  const [savedRecentListings, setSavedRecentListings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isWhitelabel, partner_id } = useWhitelabel();
  const { userCurrency } = useSelector(
    ({ appData }: AppState) => ({
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );

  const getInventoriesData = async (listingNames: any) => {
    const LISTINGS_TO_SHOW = 10;
    const options = {
      params: {
        canonical_names: listingNames.toString(),
        only: "id,status,name,canonical_name,location,pricing,tags,virtual_views,videos",
        methods:
          "children_count,reviews_rating,reviews_count,top_meta_distances,amber_sales,meta_short",
        limit: LISTINGS_TO_SHOW,
        ...(isWhitelabel && { get_all_whitelabel_partner_inventories: partner_id }),
        ...(isWhitelabel && { exclude_whitelabel_inventories: false }),
        ...(userCurrency && { user_currency: userCurrency }),
      },
    };

    setSavedRecentListings(listingNames);
    const res = await getInventories(options);
    if (res.data) {
      if (res?.data?.data.result) {
        res.data.data.result = filterActiveChildInventories(res.data.data.result);
        setRecentListings(res?.data?.data.result || []);
      }
      setIsLoading(false);
    } else {
      setRecentListings([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const LISTINGS_TO_SHOW = 10;
    const listingNames: string[] = getLastViewedListings(1, LISTINGS_TO_SHOW) || [];

    if (listingNames && listingNames?.length && !recentListings?.length) {
      getInventoriesData(listingNames);
    } else {
      setRecentListings([]);
      setIsLoading(false);
    }
  }, []);

  const getListings = useCallback(() => {
    const listings = recentListings.filter(
      (listing: any) =>
        !(
          typeof listing.images === "undefined" ||
          (listing.images && listing.images.length === 0) ||
          listing.status !== "active"
        ),
    );
    return listings.length > 0
      ? listings
          .filter(
            (item: any) =>
              savedRecentListings.includes(item?.canonical_name) && item?.canonical_name !== "",
          )
          .sort((a: any, b: any) => {
            const indexA = savedRecentListings.indexOf(a?.canonical_name);
            const indexB = savedRecentListings.indexOf(b?.canonical_name);
            return indexA - indexB;
          })
      : [];
  }, [recentListings.length, savedRecentListings.length]);
  const listings = !isLoading ? getListings() : defaultInventoryState;
  if (!isSearchPage) {
    listings.shift();
  }
  const { t } = useTranslationHook();
  const recentlyViewedText = t("propertypage.recentelyviewedsection.recentelyviewedsectiontitle");

  return (
    <>
      {listings.length > 0 && (
        <SectionContainer
          dataTestId="searchPage-recentlyViewed-section"
          title={recentlyViewedText}
          id="recently_viewed_section"
          className={formatClassNames(
            classes.container,
            isDesktop ? classes.desktop : "",
            isDesktop && isSearchPage ? classes.searchPageMargins : "",
            isSearchPageRails && classes.searchPageRails,
          )}
          isDesktop={isDesktop}
          showTitleLine
          headingLevel={2}
        >
          <HorizontalScroll
            className={formatClassNames(
              classes.scrollContainer,
              isDesktop ? classes.scrollContainerDesktop : "",
            )}
            showControls={isDesktop}
          >
            {listings.map((item) => (
              <CompactInventoryCard
                item={item}
                key={item.id}
                isRequestActive={isLoading}
                gtagEventAction="clicked_recently_viewed_properties"
                gtagEventCategory={gtagEventCategory}
                isDesktop={isDesktop}
                showIcons
                screenName={APP_PAGE.SEARCH}
              />
            ))}
          </HorizontalScroll>
        </SectionContainer>
      )}
    </>
  );
};

export default wrapperHOC(RecentelyViewedSection, {
  componentName: "RecentelyViewedSection-SearchPage",
  showForChina: true,
});
